// Server-authoritative per-slot trail opacity. Room state supplies the complete
// engine-id map, so every viewer composites each curve at the same alpha.
import { TRAIL_OPACITY_MIN_PERCENT } from '/shared/game-engine.js';

const FULL_PERCENT = 100;

// Engine player id -> percent. Only slots that have been dimmed are in here; an
// absent slot is solid, so the map stays empty in the common case.
const slotPercents = new Map();

function clampTrailPercent(percent) {
  const value = Number(percent);
  if (!Number.isFinite(value)) return FULL_PERCENT;
  return Math.round(Math.min(FULL_PERCENT, Math.max(TRAIL_OPACITY_MIN_PERCENT, value)));
}

// `ownerId` is the ENGINE player id -- the same id trail ops carry, so the
// renderer can ask this directly about an op it is about to draw.
function slotTrailOpacityPercent(ownerId) {
  const id = Number(ownerId);
  if (!Number.isFinite(id) || id < 0) return FULL_PERCENT;
  const stored = slotPercents.get(Math.floor(id));
  return stored === undefined ? FULL_PERCENT : clampTrailPercent(stored);
}

function slotTrailOpacity(ownerId) {
  return slotTrailOpacityPercent(ownerId) / 100;
}

// Replaces the whole authoritative map. The caller re-derives engine ids from
// room state whenever the roster changes.
function setSlotTrailOpacities(entries) {
  slotPercents.clear();
  const pairs = entries instanceof Map ? entries.entries() : (Array.isArray(entries) ? entries : []);
  for (const [ownerId, percent] of pairs) {
    const id = Number(ownerId);
    if (!Number.isFinite(id) || id < 0) continue;
    const value = clampTrailPercent(percent);
    if (value >= FULL_PERCENT) continue;
    slotPercents.set(Math.floor(id), value);
  }
}

function clearSlotTrailOpacities() {
  slotPercents.clear();
}

// Everything the renderer's layout depends on, in one comparable value: which
// alphas exist and who wears them. The renderer diffs this once a frame instead
// of being told, so nothing can change an alpha without the canvases following.
function trailOpacitySignature() {
  let signature = '';
  const ids = [...slotPercents.keys()].sort((a, b) => a - b);
  for (const id of ids) signature += `|${id}:${slotPercents.get(id)}`;
  return signature;
}

export {
  FULL_PERCENT,
  clampTrailPercent,
  slotTrailOpacity,
  slotTrailOpacityPercent,
  setSlotTrailOpacities,
  clearSlotTrailOpacities,
  trailOpacitySignature
};
